import Repository from './Repository';

class GroupRepository extends Repository {
	/**
	 * Resolves an array of group objects from the API.
	 *
	 * @param {int} clientId THe client ID
	 * @returns {Promise<array>}
	 */
	async all (clientId) {
		return this.getWithToken(`/clients/${clientId}/groups`);
	}

	/**
	 * Resolves an array of group objects from the API.
	 *
	 * @param {int} clientId The client ID
	 * @param {int} page The page number to load
	 * @param {string|null} filterData
	 * @returns {Promise<array>}
	 */
	async queryGroups (clientId, page, filterData) {
		let filter = '';
		if (filterData) {
			filter = `&filter=${filterData}`;
		}

		return this.getWithToken(
			`/clients/${clientId}/groups/query?page=${page}${filter}`
		);
	}
	/**
	 * Resolves a single group from the given ID.
	 *
	 * @param {int} clientId The client ID
	 * @param {int} id The group ID to retrieve
	 * @returns {Promise<object>}
	 */
	async find (clientId, id) {
		return this.getWithToken(`/clients/${clientId}/groups/${id}`);
	}

	/**
	 * Resolves a single group with joined device types from the given ID.
	 *
	 * @param {int} clientId The client ID
	 * @param {int} id The group ID to retrieve
	 * @returns {Promise<object>}
	 */
	async findWithDeviceTypes (clientId, id) {
		return this.getWithToken(`/clients/${clientId}/groups/${id}?include=deviceTypes&append=monitor_count`);
	}

	/**
	 * Stores a new group under the given client ID.
	 *
	 * @param {int} clientId The client ID
	 * @param {string} name The name of the group to store
	 * @param {int|null} parentId The parent ID of the group to store.
	 * @param {boolean} isActive Active flag of the group to store
	 * @returns {Promise<object>}
	 */
	async store (clientId, name, parentId, isActive) {
		return this.postWithToken(`/clients/${clientId}/groups`, {
			name,
			parent_group_id: parentId,
			is_active: isActive
		});
	}

	/**
	 * Updates the given group under the given client ID.
	 *
	 * @param {int} clientId The client ID
	 * @param {string} name The name of the group to store
	 * @param {int|null} parentId The parent ID of the group to store.
	 * @param {boolean} isActive Active flag of the group to store
	 * @returns {Promise<object>}
	 */
	async update (groupId, clientId, name, parentId, isActive) {
		return this.putWithToken(`/clients/${clientId}/groups/${groupId}`, {
			name,
			parent_group_id: parentId,
			is_active: isActive
		});
	}

	/**
	 * Retrieves an array of monitors from the given client and group ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {int} groupId The group ID to query against
	 * @returns {Promise<array>}
	 */
	async getMonitors (clientId, groupId) {
		const include = [
			'threshold-setting',
			'monitor-action-group-template.monitor-action-groups.success-action',
			'monitor-action-group-template.monitor-action-groups.failure-action'
			// 'audits',
			// 'audits.monitorAudits',
			// 'audits.monitorAudits.monitored',
			// 'notifications',
			// 'notifications.monitored'
		].join(',');

		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/monitors?include=-${include}`);
	}

	/**
	 * Retrieves an array of monitor types from the given group ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {int} groupId The group ID to query against
	 */
	async getMonitorTypes (clientId, groupId) {
		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/monitors/types`);
	}

	/**
	 * Retrieves the given monitor type from the given client ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {string} monitorType The monitor type key
	 * @param {object} settings The settings to be sent with the query
	 */
	async getMonitorType (clientId, groupId, monitorType, settings) {
		const query = Object.keys(settings)
			.filter(key => settings[key] !== '')
			.map(key => key + '=' + settings[key]).join('&');

		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/monitors/types/${monitorType}?${query}`);
	}

	/**
	 * Retrieves an array of inherited monitors from the given client and group ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {int} groupId The group ID to query against
	 * @returns {Promise<array>}
	 */
	async getInheritedMonitors (clientId, groupId) {
		const include = [
			'threshold-setting',
			'monitor-action-group-template.monitor-action-groups.success-action',
			'monitor-action-group-template.monitor-action-groups.failure-action'
			// 'notifications',
			// 'notifications.monitored',
			// 'audits',
			// 'audits.monitorAudits',
			// 'audits.monitorAudits.monitored'
		].join(',');

		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/monitors/inherited?include=${include}`);
	}

	/**
	 * retrieves the notifications for a group
	 *
	 * @param clientId
	 * @returns {Promise<Array|Object|string>}
	 */
	async getNotifications (groupId) {
		return this.getWithToken(`/groups/${groupId}/notifications`);
	}

	refreshDeviceAll (groupId, type) {
		return this.putWithToken(`/groups/${groupId}/refresh/all`, { type: type });
	}

	getGroups (groupId) {
		return this.getWithToken(`/groups/${groupId}/groups`);
	}

	getDevices (groupId) {
		return this.getWithToken(`/groups/${groupId}/devices`);
	}

	activateGroup (groupId, request) {
		return this.patchWithToken(`/groups/${groupId}/activate`, request);
	}

	deactivateGroup (groupId) {
		return this.patchWithToken(`/groups/${groupId}/deactivate`);
	}

	deleteGroup (id, group) {
		return this.deleteWithToken(`/groups/${id}?group=${group}`);
	}
}

export default new GroupRepository();
